import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { CatalogFilters } from '../components/CatalogFilters';
import { API, collectionLabels } from '../lib/catalog';
import { PageIntro } from '../components/PageIntro';
import { BlurFade } from '../components/magicui/blur-fade';

export default function WebShop() {
  const [params, setParams] = useSearchParams();
  const collection = params.get('collection') || '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const sort = params.get('sort') || 'name';
  const query = params.get('q') || '';
  const [search, setSearch] = useState(query);
  const [data, setData] = useState({ items: [], total: 0, collections: Object.entries(collectionLabels).map(([id, name]) => ({ id, name, count: 0 })) });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const { addToCart } = useCart();

  const update = (values) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    setParams(next, { preventScrollReset: true });
  };
  useEffect(() => { setSearch(query); }, [query]);
  useEffect(() => {
    if (search === query) return;
    const timer = setTimeout(() => {
      setParams(previous => {
        const next = new URLSearchParams(previous);
        search ? next.set('q', search) : next.delete('q');
        next.delete('page');
        return next;
      }, { replace: true, preventScrollReset: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, query, setParams]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    axios.get(`${API}/catalog/products`, { params: { collection, page, sort, q: query }, signal: controller.signal })
      .then(response => setData(response.data))
      .catch(err => { if (!axios.isCancel(err)) setError('Greška pri učitavanju proizvoda. Pokušajte ponovo.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [collection, page, sort, query, retry]);
  const add = product => {
    addToCart(product);
    toast({ title: 'Dodano u košaricu', description: product.name });
  };
  const pages = Math.max(1, Math.ceil(data.total / 24));
  const changePage = next => { update({ page: String(next) }); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="premium-page">
      <div className="premium-container">
        <PageIntro title={collectionLabels[collection] || 'Rasvjeta za vaš prostor.'} testId="webshop-title">
          <span className="sr-only" data-testid="webshop-product-brand">MAXLIGHT</span>
          <p data-testid="catalog-vat-notice">Sve cijene uključuju 25 % PDV-a.</p>
        </PageIntro>
        <CatalogFilters collections={data.collections} selected={collection} onSelect={value => update({ collection: value, page: '', q: '' })} search={search} onSearch={setSearch} sort={sort} onSort={value => update({ sort: value, page: '' })} />
        {loading ? <div className="py-20 text-center" role="status" data-testid="webshop-loading"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />Učitavanje proizvoda...</div>
          : error ? <div className="py-16 text-center"><p role="alert" className="mb-4" data-testid="webshop-error">{error}</p><Button onClick={() => setRetry(retry + 1)} data-testid="webshop-retry-button">Pokušaj ponovo</Button></div>
          : <>
            <p className="text-xs text-gray-500 mb-6" aria-live="polite" data-testid="webshop-product-count">{data.total} proizvoda</p>
            <BlurFade inView={false} duration={0.3} className="premium-product-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="webshop-products">
              {data.items.map(product => <ProductCard key={product.id} product={product} onAdd={add} />)}
            </BlurFade>
            {!data.total && <p className="py-16 text-center text-gray-600" data-testid="catalog-empty-state">Nema proizvoda koji odgovaraju pretrazi.</p>}
            {pages > 1 && <div className="flex flex-wrap justify-center items-center gap-4 mt-10" data-testid="catalog-pagination">
              <Button variant="outline" disabled={page <= 1} onClick={() => changePage(page - 1)} aria-label="Prethodna stranica" data-testid="catalog-previous-page"><ChevronLeft size={18} /></Button>
              <span className="text-sm" data-testid="catalog-page-status">Stranica {page} od {pages}</span>
              <Button variant="outline" disabled={page >= pages} onClick={() => changePage(page + 1)} aria-label="Sljedeća stranica" data-testid="catalog-next-page"><ChevronRight size={18} /></Button>
            </div>}
          </>}
      </div>
    </div>
  );
}